// Miniaturas de fotos, vídeos e documentos com visualizador em tela cheia
// (foto ampliada, vídeo tocando na própria tela, PDF embutido; setas ← → para navegar).
import React, { useCallback, useEffect, useState } from "react";
import { Dialog, IconButton, Tooltip, Typography, makeStyles } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";

export const tipoMidia = (url) => {
  const u = String(url || "").split("?")[0];
  if (/\.(jpe?g|png|webp|gif|bmp|heic)$/i.test(u)) return "imagem";
  if (/\.(mp4|mov|m4v|3gp|webm|ogg)$/i.test(u)) return "video";
  if (/\.pdf$/i.test(u)) return "pdf";
  return "arquivo";
};

const useStyles = makeStyles((theme) => ({
  grade: { display: "flex", gap: 8, flexWrap: "wrap" },
  mini: {
    position: "relative", width: 104, height: 104, borderRadius: 10, overflow: "hidden", cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`, background: theme.palette.action.hover, flex: "none",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
    transition: "transform .12s, box-shadow .12s",
    "&:hover": { transform: "scale(1.03)", boxShadow: theme.shadows[3] },
    "& img, & video": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  },
  capa: { width: 104, height: 140, background: "#fff", "& img": { objectFit: "cover", objectPosition: "top" } },
  play: { position: "absolute", color: "rgba(255,255,255,.92)", fontSize: 40, filter: "drop-shadow(0 1px 3px rgba(0,0,0,.6))" },
  doc: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, fontSize: 11, color: theme.palette.text.secondary },
  etiqueta: {
    position: "absolute", left: 4, bottom: 4, fontSize: 10, color: "#fff", background: "rgba(0,0,0,.55)",
    borderRadius: 4, padding: "1px 5px",
  },
  palco: {
    background: "#111", color: "#fff", display: "flex", flexDirection: "column", height: "92vh",
    [theme.breakpoints.down("xs")]: { height: "100vh" },
  },
  barra: { display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", "& button": { color: "#fff" } },
  centro: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minHeight: 0 },
  midia: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
  seta: {
    position: "absolute", top: "50%", transform: "translateY(-50%)", color: "#fff", background: "rgba(255,255,255,.12)",
    "&:hover": { background: "rgba(255,255,255,.25)" },
  },
}));

// itens: [{ url, titulo, capa? }] — capa: imagem da 1ª página (PDF de manual)
const Galeria = ({ itens }) => {
  const c = useStyles();
  const [aberto, setAberto] = useState(null);
  const total = itens.length;
  const ir = useCallback((d) => setAberto((i) => (i === null ? i : (i + d + total) % total)), [total]);

  useEffect(() => {
    if (aberto === null) return undefined;
    const tecla = (e) => {
      if (e.key === "ArrowRight") ir(1);
      if (e.key === "ArrowLeft") ir(-1);
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [aberto, ir]);

  if (!total) return null;
  const atual = aberto !== null ? itens[aberto] : null;
  const tipo = atual && tipoMidia(atual.url);
  return (
    <>
      <div className={c.grade}>
        {itens.map((it, i) => {
          const t = tipoMidia(it.url);
          return (
            <Tooltip key={`${it.url}-${i}`} title={it.titulo || ""}>
              <button type="button" className={`${c.mini} ${it.capa ? c.capa : ""}`} onClick={() => setAberto(i)}>
                {t === "imagem" && <img src={it.url} alt={it.titulo || "foto"} loading="lazy" />}
                {it.capa && <img src={it.capa} alt={`capa de ${it.titulo || "documento"}`} loading="lazy" />}
                {t === "video" && (
                  <>
                    <video src={`${it.url}#t=0.5`} preload="metadata" muted playsInline />
                    <PlayCircleFilledIcon className={c.play} />
                  </>
                )}
                {(t === "pdf" || t === "arquivo") && !it.capa && (
                  <div className={c.doc}><DescriptionOutlinedIcon style={{ fontSize: 36 }} />{t === "pdf" ? "PDF" : "Arquivo"}</div>
                )}
                {t !== "imagem" && <span className={c.etiqueta}>{t === "video" ? "vídeo" : t === "pdf" ? "pdf" : "arquivo"}</span>}
              </button>
            </Tooltip>
          );
        })}
      </div>
      <Dialog open={aberto !== null} onClose={() => setAberto(null)} maxWidth="lg" fullWidth>
        {atual && (
          <div className={c.palco}>
            <div className={c.barra}>
              <Typography variant="body2" style={{ flex: 1 }} noWrap>
                {`${atual.titulo || ""}${total > 1 ? `  ·  ${aberto + 1} de ${total}` : ""}`}
              </Typography>
              <Tooltip title="Abrir em nova aba / baixar">
                <IconButton size="small" component="a" href={atual.url} target="_blank" rel="noopener noreferrer"><OpenInNewIcon /></IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setAberto(null)}><CloseIcon /></IconButton>
            </div>
            <div className={c.centro}>
              {tipo === "imagem" && <img src={atual.url} alt={atual.titulo || "foto"} className={c.midia} />}
              {tipo === "video" && <video key={atual.url} src={atual.url} controls autoPlay playsInline className={c.midia} />}
              {tipo === "pdf" && <iframe title="pdf" src={atual.url} style={{ width: "100%", height: "100%", border: 0, background: "#fff" }} />}
              {tipo === "arquivo" && (
                <Typography>
                  Este arquivo não tem visualização. <a href={atual.url} target="_blank" rel="noopener noreferrer" style={{ color: "#90caf9" }}>Baixar</a>
                </Typography>
              )}
              {total > 1 && (
                <>
                  <IconButton className={c.seta} style={{ left: 8 }} onClick={() => ir(-1)}><ChevronLeftIcon fontSize="large" /></IconButton>
                  <IconButton className={c.seta} style={{ right: 8 }} onClick={() => ir(1)}><ChevronRightIcon fontSize="large" /></IconButton>
                </>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default Galeria;
